/**
 * Multiplayer Game State Store - Simultaneous-turn augmented-stone-flicking
 *
 * Event sourcing with simultaneous turn model:
 * - All players submit inputs secretly during INPUT_PHASE
 * - Host manages 15s timer and resolves round when all inputs received
 * - Resolution plays animation, then starts next INPUT_PHASE
 */

import type {
  PlayerId,
  ResolveRoundAction,
  SkipFlickAction,
  StoneFlickingAction,
  StoneFlickingConfig,
  StoneFlickingState,
  SubmitFlickAction,
  SurrenderAction,
  ValidationResult,
} from "@cloudnest/augmented-stone-flicking-engine";
import {
  applyResolveRound,
  applySkipFlick,
  applySubmitFlick,
  applySurrender,
  buildCoreConfig,
  createConfigForPlayerCount,
  DEFAULT_STONE_FLICKING_CONFIG,
  initGame,
  validateResolveRound,
  validateSkipFlick,
  validateSubmitFlick,
  validateSurrender,
} from "@cloudnest/augmented-stone-flicking-engine";
import type {
  StoneFlickingCoreConfig,
  TransitionRecording,
} from "@cloudnest/stone-flicking-core-engine/types";
import { useSyncExternalStore } from "react";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
  type GameEvent,
} from "../../shared/services/game-api";
import { resolveAugmentedStoneFlickingGameApiMode } from "./route-mode";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function getGameApi(): GameApi {
  const nextMode = resolveAugmentedStoneFlickingGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("augmented-stone-flicking", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

/**
 * Reconstitute pendingInputs Map from JSON-deserialized state.
 * JSON.stringify converts Map to {}, which breaks new Map(obj).
 */
function hydrateGameState(raw: StoneFlickingState): StoneFlickingState {
  const pending = raw.coreState.pendingInputs;
  if (pending instanceof Map) return raw;

  const entries = Object.entries(pending as unknown as Record<string, unknown>);
  return {
    ...raw,
    coreState: {
      ...raw.coreState,
      pendingInputs: new Map(
        entries.map(([k, v]) => [k, v]),
      ) as StoneFlickingState["coreState"]["pendingInputs"],
    },
  };
}

interface MultiplayerGameState {
  readonly gameState: StoneFlickingState | null;
  readonly config: StoneFlickingConfig;
  readonly coreConfig: StoneFlickingCoreConfig;
  readonly roomId: string | null;
  readonly myPlayerId: PlayerId | null;
  readonly isHost: boolean;
  readonly lastAppliedSeq: number;
  readonly lastError: string | null;
  readonly selectedEntityId: string | null;
  readonly confirmedInput: {
    readonly entityId: string;
    readonly impulse: { readonly x: number; readonly y: number };
  } | null;
  readonly readyPlayers: ReadonlyArray<PlayerId>;
  readonly isWaitingForOthers: boolean;
  readonly roundDeadline: number | null;
  readonly isAnimating: boolean;
  readonly animationRecording: TransitionRecording | null;
}

const defaultConfig = DEFAULT_STONE_FLICKING_CONFIG;

const initialState: MultiplayerGameState = {
  gameState: null,
  config: defaultConfig,
  coreConfig: buildCoreConfig(defaultConfig),
  roomId: null,
  myPlayerId: null,
  isHost: false,
  lastAppliedSeq: 0,
  lastError: null,
  selectedEntityId: null,
  confirmedInput: null,
  readyPlayers: [],
  isWaitingForOthers: false,
  roundDeadline: null,
  isAnimating: false,
  animationRecording: null,
};

let state: MultiplayerGameState = { ...initialState };

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

function validateAction(
  gameState: StoneFlickingState,
  action: StoneFlickingAction,
  coreConfig: StoneFlickingCoreConfig,
): ValidationResult {
  switch (action.type) {
    case "SUBMIT_FLICK":
      return validateSubmitFlick(gameState, action, coreConfig);
    case "SKIP_FLICK":
      return validateSkipFlick(gameState, action);
    case "RESOLVE_ROUND":
      return validateResolveRound(gameState, action);
    case "SURRENDER":
      return validateSurrender(gameState, action);
    default:
      return { valid: false, error: "Unknown action type" };
  }
}

function applyAction(
  gameState: StoneFlickingState,
  action: StoneFlickingAction,
  coreConfig: StoneFlickingCoreConfig,
): StoneFlickingState | null {
  const validation = validateAction(gameState, action, coreConfig);
  if (!validation.valid) return null;

  switch (action.type) {
    case "SUBMIT_FLICK":
      return applySubmitFlick(gameState, action);
    case "SKIP_FLICK":
      return applySkipFlick(gameState, action);
    case "RESOLVE_ROUND":
      return applyResolveRound(gameState, coreConfig);
    case "SURRENDER":
      return applySurrender(gameState, action);
    default:
      return null;
  }
}

const ROUND_TIMEOUT_MS = 15_000;

/**
 * Fold a persisted event log into the final stone-flicking state. Shared by
 * `replayEvents` (guest/reconnect sync) and the host-reconnect guard in
 * `initializeAsHost`, so both reconstruct the same state from the same log.
 * Carries forward the config: the `init` event payload may override the base
 * config (and thus the derived core config).
 */
function replayAsfEvents(
  events: readonly GameEvent[],
  initialConfig: StoneFlickingConfig,
  initialCoreConfig: StoneFlickingCoreConfig,
): {
  gameState: StoneFlickingState | null;
  config: StoneFlickingConfig;
  coreConfig: StoneFlickingCoreConfig;
  lastSeq: number;
} {
  let currentState: StoneFlickingState | null = null;
  let cfg = initialConfig;
  let coreCfg = initialCoreConfig;
  let lastSeq = 0;

  for (const event of events) {
    if (event.eventType === "init") {
      currentState = hydrateGameState(
        event.payload.state as unknown as StoneFlickingState,
      );
      if (event.payload.config) {
        cfg = event.payload.config as unknown as StoneFlickingConfig;
        coreCfg = buildCoreConfig(cfg);
      }
      lastSeq = event.sequenceNumber;
    } else if (event.eventType === "action" && currentState) {
      const action = event.payload as unknown as StoneFlickingAction;
      const newState = applyAction(currentState, action, coreCfg);
      if (newState) {
        currentState = newState;
      }
      lastSeq = event.sequenceNumber;
    } else if (event.eventType === "game_over") {
      lastSeq = event.sequenceNumber;
    }
  }

  return { gameState: currentState, config: cfg, coreConfig: coreCfg, lastSeq };
}

export const multiplayerGameActions = {
  /**
   * Initialize game as host. Fetches the persisted event log FIRST: if any
   * events exist the host is reconnecting (reload / remount), so REPLAY them
   * instead of submitting a new "init" — which would overwrite the in-progress
   * game and reset it to the start. Only the new-game path (empty log) inits.
   */
  initializeAsHost: async (
    roomId: string,
    assignedPlayerId: PlayerId = "P1",
    playerCount?: 2 | 3 | 4,
  ): Promise<void> => {
    state = {
      ...state,
      gameState: null,
      roomId,
      myPlayerId: assignedPlayerId,
      isHost: true,
      lastError: null,
      selectedEntityId: null,
      confirmedInput: null,
      isWaitingForOthers: false,
      isAnimating: false,
      animationRecording: null,
    };
    notify();

    const eventsResponse = await getGameApi().getGameEvents(roomId);
    const existingEvents = eventsResponse.success
      ? (eventsResponse.data ?? [])
      : [];

    if (existingEvents.length > 0) {
      const replay = replayAsfEvents(
        existingEvents,
        state.config,
        state.coreConfig,
      );
      state = {
        ...state,
        gameState: replay.gameState,
        config: replay.config,
        coreConfig: replay.coreConfig,
        lastAppliedSeq: replay.lastSeq,
        lastError: null,
        readyPlayers: replay.gameState?.submittedPlayers ?? [],
        roundDeadline:
          replay.gameState?.phase === "PLAYING"
            ? Date.now() + ROUND_TIMEOUT_MS
            : null,
      };
      notify();
      return;
    }

    // No events yet — brand-new game: create initial state and persist it.
    const cfg = playerCount
      ? createConfigForPlayerCount(playerCount)
      : state.config;
    const coreCfg = buildCoreConfig(cfg);

    const gameState = initGame({ config: cfg, hostPlayerId: assignedPlayerId });

    state = {
      ...state,
      gameState,
      config: cfg,
      coreConfig: coreCfg,
      lastAppliedSeq: 0,
      lastError: null,
      selectedEntityId: null,
      confirmedInput: null,
      readyPlayers: [],
      isWaitingForOthers: false,
      roundDeadline: Date.now() + ROUND_TIMEOUT_MS,
      isAnimating: false,
      animationRecording: null,
    };
    notify();

    const response = await getGameApi().submitEvent(roomId, "init", {
      state: gameState as unknown as Record<string, unknown>,
      config: cfg as unknown as Record<string, unknown>,
    });

    if (response.success && response.data) {
      state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
      notify();
    }
  },

  initializeAsGuest: (
    roomId: string,
    assignedPlayerId: PlayerId = "P2",
  ): void => {
    state = {
      ...state,
      gameState: null,
      roomId,
      myPlayerId: assignedPlayerId,
      isHost: false,
      lastAppliedSeq: 0,
      lastError: null,
      selectedEntityId: null,
      confirmedInput: null,
      readyPlayers: [],
      isWaitingForOthers: false,
      roundDeadline: null,
      isAnimating: false,
      animationRecording: null,
    };
    notify();
  },

  replayEvents: async (roomId: string): Promise<void> => {
    const response = await getGameApi().getGameEvents(roomId);

    if (!response.success || !response.data) {
      state = { ...state, lastError: "이벤트를 불러올 수 없습니다" };
      notify();
      return;
    }

    const replay = replayAsfEvents(
      response.data,
      state.config,
      state.coreConfig,
    );

    const myPlayerId = state.myPlayerId ?? "P1";

    state = {
      ...state,
      gameState: replay.gameState,
      config: replay.config,
      coreConfig: replay.coreConfig,
      myPlayerId,
      lastAppliedSeq: replay.lastSeq,
      lastError: null,
      readyPlayers: replay.gameState?.submittedPlayers ?? [],
      roundDeadline:
        replay.gameState?.phase === "PLAYING"
          ? Date.now() + ROUND_TIMEOUT_MS
          : null,
    };
    notify();
  },

  applyRemoteEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;

    if (eventType === "init") {
      const initState = hydrateGameState(
        payload.state as unknown as StoneFlickingState,
      );
      const myPlayerId = state.myPlayerId ?? "P1";

      let cfg = state.config;
      let coreCfg = state.coreConfig;
      if (payload.config) {
        cfg = payload.config as unknown as StoneFlickingConfig;
        coreCfg = buildCoreConfig(cfg);
      }

      state = {
        ...state,
        gameState: initState,
        config: cfg,
        coreConfig: coreCfg,
        myPlayerId,
        lastAppliedSeq: seq,
        roundDeadline: Date.now() + ROUND_TIMEOUT_MS,
      };
      notify();
      return;
    }

    if (eventType === "action" && state.gameState) {
      const action = payload as unknown as StoneFlickingAction;
      const newState = applyAction(state.gameState, action, state.coreConfig);
      if (newState) {
        const isResolve = action.type === "RESOLVE_ROUND";
        const hasRecording =
          isResolve && newState.coreState.lastRecording !== null;

        state = {
          ...state,
          gameState: newState,
          lastAppliedSeq: seq,
          readyPlayers: newState.submittedPlayers,
          isAnimating: hasRecording,
          animationRecording: hasRecording
            ? newState.coreState.lastRecording
            : state.animationRecording,
          confirmedInput: isResolve ? null : state.confirmedInput,
          isWaitingForOthers: isResolve ? false : state.isWaitingForOthers,
          roundDeadline:
            isResolve && newState.phase === "PLAYING"
              ? Date.now() + ROUND_TIMEOUT_MS
              : state.roundDeadline,
        };
        notify();
      }
      return;
    }

    if (eventType === "game_over") {
      state = { ...state, lastAppliedSeq: seq };
      notify();
    }
  },

  confirmInput: async (
    entityId: string,
    impulse: { x: number; y: number },
  ): Promise<boolean> => {
    const { gameState, roomId, myPlayerId, coreConfig } = state;
    if (!gameState || !roomId || !myPlayerId) return false;

    const action: SubmitFlickAction = {
      type: "SUBMIT_FLICK",
      playerId: myPlayerId,
      entityId,
      impulse,
    };

    const validation = validateSubmitFlick(gameState, action, coreConfig);
    if (!validation.valid) {
      state = {
        ...state,
        lastError: validation.error ?? "유효하지 않은 행동입니다",
      };
      notify();
      return false;
    }

    const previousState = gameState;
    const newState = applySubmitFlick(gameState, action);

    state = {
      ...state,
      gameState: newState,
      lastError: null,
      selectedEntityId: null,
      confirmedInput: { entityId, impulse },
      readyPlayers: newState.submittedPlayers,
      isWaitingForOthers: true,
    };
    notify();

    const response = await getGameApi().submitEvent(
      roomId,
      "action",
      action as unknown as Record<string, unknown>,
    );

    if (!response.success) {
      state = {
        ...state,
        gameState: previousState,
        confirmedInput: null,
        isWaitingForOthers: false,
        readyPlayers: previousState.submittedPlayers,
        lastError: response.error ?? "행동 실패",
      };
      notify();
      return false;
    }

    if (response.data) {
      state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
      notify();
    }

    return true;
  },

  handleTimeout: async (): Promise<void> => {
    if (!state.gameState || !state.roomId || !state.isHost) return;
    if (state.gameState.phase !== "PLAYING") return;

    const roomId = state.roomId;

    // Re-read state for each skip to avoid desync with remote events
    const pendingPlayers = state.gameState.coreState.participants
      .map((p) => p.id)
      .filter(
        (id) =>
          !state.gameState!.coreState.eliminatedParticipants.includes(id) &&
          !state.gameState!.coreState.submittedParticipants.includes(id),
      );

    for (const pid of pendingPlayers) {
      // Re-check: player may have submitted between iterations
      const current = state.gameState;
      if (!current || current.phase !== "PLAYING") return;
      if (current.coreState.submittedParticipants.includes(pid)) continue;

      const skipAction: SkipFlickAction = {
        type: "SKIP_FLICK",
        playerId: pid as PlayerId,
      };

      const newState = applySkipFlick(current, skipAction);
      state = {
        ...state,
        gameState: newState,
        readyPlayers: newState.submittedPlayers,
      };
      notify();

      const skipResponse = await getGameApi().submitEvent(
        roomId,
        "action",
        skipAction as unknown as Record<string, unknown>,
      );
      // Advance lastAppliedSeq so that the corresponding WS echo is filtered
      // out. Without this, the remote skip event arrives at the new round's
      // state and marks participants as submitted, causing a spurious early
      // resolve when the remote RESOLVE_ROUND event arrives next.
      if (skipResponse.success && skipResponse.data) {
        state = { ...state, lastAppliedSeq: skipResponse.data.sequenceNumber };
      }
    }

    await multiplayerGameActions.resolveRound();
  },

  resolveRound: async (): Promise<void> => {
    const { gameState, roomId, coreConfig } = state;
    if (!gameState || !roomId || !state.isHost) return;

    const resolveAction: ResolveRoundAction = {
      type: "RESOLVE_ROUND",
      resolverId: state.myPlayerId ?? "",
    };
    const validation = validateResolveRound(gameState, resolveAction);
    if (!validation.valid) return;

    const newState = applyResolveRound(gameState, coreConfig);

    const hasRecording = newState.coreState.lastRecording !== null;

    state = {
      ...state,
      gameState: newState,
      confirmedInput: null,
      readyPlayers: [],
      isWaitingForOthers: false,
      isAnimating: hasRecording,
      animationRecording: hasRecording
        ? newState.coreState.lastRecording
        : null,
      roundDeadline:
        newState.phase === "PLAYING" ? Date.now() + ROUND_TIMEOUT_MS : null,
    };
    notify();

    const response = await getGameApi().submitEvent(
      roomId,
      "action",
      resolveAction as unknown as Record<string, unknown>,
    );

    if (response.success && response.data) {
      state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
      notify();
    }

    if (newState.phase === "GAME_END") {
      await getGameApi().submitEvent(roomId, "game_over", {
        winnerId: newState.isDraw ? null : newState.winner,
        isDraw: newState.isDraw,
        reason: newState.coreState.outcomeReason,
      });
    }
  },

  submitSurrender: async (): Promise<void> => {
    const { roomId, myPlayerId, gameState, coreConfig } = state;
    if (!roomId || !myPlayerId || !gameState) return;

    const action: SurrenderAction = {
      type: "SURRENDER",
      playerId: myPlayerId,
    };

    const newState = applyAction(gameState, action, coreConfig);
    if (!newState) return;

    state = {
      ...state,
      gameState: newState,
      lastError: null,
      selectedEntityId: null,
      confirmedInput: null,
    };
    notify();

    await getGameApi().submitEvent(
      roomId,
      "action",
      action as unknown as Record<string, unknown>,
    );

    if (newState?.phase === "GAME_END") {
      await getGameApi().submitEvent(roomId, "game_over", {
        winnerId: newState.isDraw ? null : newState.winner,
        isDraw: newState.isDraw,
        reason: "CONCEDE",
      });
    }
  },

  selectEntity: (entityId: string | null): void => {
    state = { ...state, selectedEntityId: entityId, lastError: null };
    notify();
  },

  finishAnimation: (): void => {
    state = {
      ...state,
      isAnimating: false,
      animationRecording: null,
    };
    notify();
  },

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    state = { ...initialState };
    notify();
  },
};

export const multiplayerGameStore = {
  getState: (): MultiplayerGameState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useMultiplayerGame(): MultiplayerGameState {
  return useSyncExternalStore(
    multiplayerGameStore.subscribe,
    multiplayerGameStore.getState,
  );
}

export function useMultiplayerGameActions(): typeof multiplayerGameActions {
  return multiplayerGameActions;
}
